import { BrushFill as sBrushFill } from "@evergis/sgis/es/symbols/polygon/BrushFill";
import { Color } from "@evergis/color";
import { PolygonSymbol, FillBrushType, HatchBrush, isHatchBrush } from "../../interfaces/style";
import { getParameterValue } from "../../evergis/core/classification/getFromClassified";
import { getLineDash } from "./SymbolRenderer/strokeStyle";
import { getHexColor } from "../../utils/color";
import { clamp } from "../../utils/math";

const sum = (numbers: number[]) => numbers.reduce((result, num) => result + num, 0);

const shouldFillMatrixCell = (matrixSize: number, hatchAngle: number, x: number, y: number) => {
  const center = Math.round(matrixSize / 2);

  if ((hatchAngle === 0 || hatchAngle === Math.PI) && x === center) {
    return true;
  }
  if (hatchAngle === Math.PI / 4 && y === x) {
    return true;
  }
  if (hatchAngle === Math.PI / 2 && y === center) {
    return true;
  }
  if (hatchAngle === (3 * Math.PI) / 4 && matrixSize - 1 - y === x) {
    return true;
  }

  return false;
};

const createEmptyMatrix = (size: number): number[][] =>
  Array(size)
    .fill(null)
    .map(() => Array(size));

const fillMatrix = (matrix: number[][], filler: (x: number, y: number, size: number) => number) => {
  const matrixSize = matrix.length;

  for (let x = 0; x < matrixSize; x++) {
    for (let y = 0; y < matrixSize; y++) {
      matrix[x][y] = filler(x, y, matrixSize);
    }
  }
};

const createFillBrushMatrix = (hatchAngle: number, hatchWidth: number, scalingFactor: number[]) => {
  const matrixSize = Math.max(Math.round(sum([Math.max(...scalingFactor), hatchWidth])), hatchWidth);
  const matrix = createEmptyMatrix(matrixSize);

  fillMatrix(matrix, (x, y, size) => (shouldFillMatrixCell(size, hatchAngle, x, y) ? 255 : 0));

  return matrix;
};

const createSymbolFillBrush = (fillBrush: FillBrushType): number[][] | undefined => {
  if (fillBrush.type !== "hatch") return undefined;

  return createFillBrushMatrix(
    getParameterValue(fillBrush.hatchAngle),
    Math.round(fillBrush.hatchWidth),
    fillBrush.scalingFactor,
  );
};

export class SGisBrushFill extends sBrushFill {
  constructor(public originalSymbol: PolygonSymbol<HatchBrush>) {
    super({
      strokeColor: getHexColor(getParameterValue(originalSymbol.stroke?.color)),
      strokeWidth: getParameterValue(originalSymbol.stroke?.width),
      lineDash: getLineDash(originalSymbol.stroke),
      fillBackground: getHexColor(getParameterValue(originalSymbol.fill?.color)),
      fillForeground: isHatchBrush(originalSymbol.fill)
        ? getHexColor(getParameterValue(originalSymbol.fill?.hatchColor))
        : void 0,
      fillBrush: createSymbolFillBrush(originalSymbol.fill),
      lineCap: "square",
    });
  }

  _updateBrush() {
    // TODO
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!this._initialized) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const brush = this.fillBrush;
    const brushSize = brush.length;
    const foreground = new Color(this.fillForeground);
    const background = new Color(this.fillBackground);
    const baseAlpha = background.a / 255;

    canvas.height = brushSize;
    canvas.width = brushSize;
    for (let i = 0; i < brushSize; i++) {
      // eslint-disable-next-line id-length
      for (let j = 0; j < brush[i].length; j++) {
        const foregroundA = clamp(brush[i][j] ? foreground.a / brush[i][j] : 0, 0, 255);

        const mixedColor = [];

        mixedColor[3] = 1 - (1 - foregroundA) * (1 - baseAlpha);
        mixedColor[0] =
          (foreground.r * foregroundA) / mixedColor[3] + (background.r * baseAlpha * (1 - foregroundA)) / mixedColor[3];
        mixedColor[1] =
          (foreground.g * foregroundA) / mixedColor[3] + (background.g * baseAlpha * (1 - foregroundA)) / mixedColor[3];
        mixedColor[2] =
          (foreground.b * foregroundA) / mixedColor[3] + (background.b * baseAlpha * (1 - foregroundA)) / mixedColor[3];

        ctx.fillStyle = `rgba(${Math.round(mixedColor[0]) || 0},${Math.round(mixedColor[1]) || 0},${
          Math.round(mixedColor[2]) || 0
        },${mixedColor[3]})`;

        ctx.fillRect(j, i, 1, 1);
      }
    }
    this._brush = new Image();
    this._brush.src = canvas.toDataURL();
  }

  clone() {
    return new SGisBrushFill(this.originalSymbol);
  }
}
